import React, { useState } from 'react';
import { Database } from 'lucide-react';
import GenericCRUDPage from '../../components/GenericCRUDPage';
import { FormField, Input, Select, Badge } from '../../components/UI';
import { brandService, manufacturerService, strengthService, taxService, currencyService } from '../../api/services';
import Breadcrumb from '../../components/Breadcrumb';

// --- Brand ---
const BrandFields = ({ form: { register } }) => (
  <div className="space-y-3">
    <FormField label="Brand Name" required><Input {...register('name', { required: true })} placeholder="Brand name" /></FormField>
    <FormField label="Description"><Input {...register('description')} placeholder="Optional description" /></FormField>
  </div>
);
const brandCols = [
  { key: 'name', label: 'Brand Name', sortable: true },
  { key: 'description', label: 'Description' },
  { key: 'isActive', label: 'Status', render: (v) => <Badge color={v ? 'green' : 'gray'}>{v ? 'Active' : 'Inactive'}</Badge> },
];

// --- Manufacturer ---
const MfgFields = ({ form: { register } }) => (
  <div className="grid grid-cols-2 gap-3">
    <FormField label="Name" required><Input {...register('name', { required: true })} placeholder="Manufacturer name" /></FormField>
    <FormField label="Country"><Input {...register('country')} placeholder="Country" /></FormField>
    <FormField label="Phone"><Input {...register('phone')} placeholder="Phone" /></FormField>
    <FormField label="Email"><Input type="email" {...register('email')} placeholder="Email" /></FormField>
    <FormField label="Website"><Input {...register('website')} placeholder="Website" /></FormField>
    <FormField label="Address"><Input {...register('address')} placeholder="Address" /></FormField>
  </div>
);
const mfgCols = [
  { key: 'name', label: 'Manufacturer', sortable: true },
  { key: 'country', label: 'Country' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'isActive', label: 'Status', render: (v) => <Badge color={v ? 'green' : 'gray'}>{v ? 'Active' : 'Inactive'}</Badge> },
];

// --- Strength ---
const StrengthFields = ({ form: { register } }) => (
  <div className="grid grid-cols-2 gap-3">
    <FormField label="Value" required><Input {...register('value', { required: true })} placeholder="e.g. 500" /></FormField>
    <FormField label="Unit">
      <Select {...register('unit')}>
        {['mg', 'g', 'ml', 'mcg', 'IU', 'mEq', '%'].map(u => <option key={u} value={u}>{u}</option>)}
      </Select>
    </FormField>
  </div>
);
const strengthCols = [
  { key: 'value', label: 'Value', sortable: true },
  { key: 'unit', label: 'Unit' },
  { key: 'isActive', label: 'Status', render: (v) => <Badge color={v ? 'green' : 'gray'}>{v ? 'Active' : 'Inactive'}</Badge> },
];

// --- Tax ---
const TaxFields = ({ form: { register } }) => (
  <div className="grid grid-cols-2 gap-3">
    <FormField label="Tax Name" required><Input {...register('name', { required: true })} placeholder="e.g. VAT" /></FormField>
    <FormField label="Rate (%)" required><Input type="number" step="0.01" min="0" max="100" {...register('rate', { required: true })} placeholder="0" /></FormField>
    <div className="col-span-2"><FormField label="Description"><Input {...register('description')} placeholder="Description" /></FormField></div>
  </div>
);
const taxCols = [
  { key: 'name', label: 'Tax Name', sortable: true },
  { key: 'rate', label: 'Rate', render: (v) => v + '%' },
  { key: 'description', label: 'Description' },
  { key: 'isActive', label: 'Status', render: (v) => <Badge color={v ? 'green' : 'gray'}>{v ? 'Active' : 'Inactive'}</Badge> },
];

// --- Currency ---
const CurrencyFields = ({ form: { register } }) => (
  <div className="grid grid-cols-2 gap-3">
    <FormField label="Currency Name" required><Input {...register('name', { required: true })} placeholder="e.g. Afghan Afghani" /></FormField>
    <FormField label="Code" required><Input {...register('code', { required: true })} placeholder="e.g. AFN" /></FormField>
    <FormField label="Symbol" required><Input {...register('symbol', { required: true })} placeholder="e.g. ؋" /></FormField>
    <FormField label="Exchange Rate"><Input type="number" step="0.0001" {...register('exchangeRate')} placeholder="1" /></FormField>
    <div className="col-span-2 flex items-center gap-2">
      <input type="checkbox" {...register('isDefault')} id="isDefault" className="rounded" />
      <label htmlFor="isDefault" className="text-sm text-gray-700 cursor-pointer">Set as Default Currency</label>
    </div>
  </div>
);
const currencyCols = [
  { key: 'name', label: 'Currency', sortable: true },
  { key: 'code', label: 'Code' },
  { key: 'symbol', label: 'Symbol' },
  { key: 'exchangeRate', label: 'Exchange Rate' },
  { key: 'isDefault', label: 'Default', render: (v) => v ? <Badge color="blue">Default</Badge> : '-' },
  { key: 'isActive', label: 'Status', render: (v) => <Badge color={v ? 'green' : 'gray'}>{v ? 'Active' : 'Inactive'}</Badge> },
];

const TABS = [
  { key: 'brands', label: 'Brands', service: brandService, entity: 'Brand', cols: brandCols, Fields: BrandFields },
  { key: 'manufacturers', label: 'Manufacturers', service: manufacturerService, entity: 'Manufacturer', cols: mfgCols, Fields: MfgFields, size: 'lg' },
  { key: 'strengths', label: 'Strengths', service: strengthService, entity: 'Strength', cols: strengthCols, Fields: StrengthFields },
  { key: 'taxes', label: 'Taxes', service: taxService, entity: 'Tax', cols: taxCols, Fields: TaxFields },
  { key: 'currencies', label: 'Currencies', service: currencyService, entity: 'Currency', cols: currencyCols, Fields: CurrencyFields },
];

export default function MasterDataPage() {
  const [tab, setTab] = useState('brands');
  const active = TABS.find(t => t.key === tab);

  return (
    <div>
      <Breadcrumb />
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
          <Database size={16} className="text-purple-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Master Data</h1>
          <p className="text-sm text-gray-500">Manage pharmacy reference data</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 bg-white border border-gray-200 rounded-xl p-1.5">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {active && (
        <GenericCRUDPage
          key={active.key}
          title={active.label}
          subtitle={'Manage ' + active.label.toLowerCase()}
          service={active.service}
          entityName={active.entity}
          columns={active.cols}
          FormFields={active.Fields}
          modalSize={active.size || 'md'}
        />
      )}
    </div>
  );
}
